/** AI 文案 · 自定义上传分组工具 */

export function createEmptyCopywritingGroup(prefix = 'cw-group') {
  return {
    id: `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    images: [],
  };
}

export function countCopywritingGroupImages(groups = []) {
  return (groups || []).reduce((sum, group) => sum + (group?.images?.length || 0), 0);
}

export function flattenCopywritingGroupImages(groups = []) {
  const list = [];
  (groups || []).forEach((group) => {
    (group?.images || []).forEach((image) => {
      if (image) list.push(image);
    });
  });
  return list;
}

export function nonEmptyCopywritingGroups(groups = []) {
  return (groups || []).filter((group) => Array.isArray(group?.images) && group.images.length > 0);
}

/** 提交任务用：解析后的 urls/previews 分组 */
export function buildCopywritingImageGroupsPayload(groups = []) {
  return nonEmptyCopywritingGroups(groups).map((group) => {
    const urls = [];
    const previews = [];
    (group.images || []).forEach((image) => {
      const apiUrl = String(image?.apiUrl || image?.previewUrl || '').trim();
      const preview = String(image?.previewUrl || image?.apiUrl || '').trim();
      if (!apiUrl && !preview) return;
      urls.push(apiUrl || preview);
      previews.push(preview || apiUrl);
    });
    return { urls, previews };
  }).filter((group) => group.urls.length > 0 || group.previews.length > 0);
}

export function extractFirstUrlsFromImageGroups(imageGroups = []) {
  const urls = [];
  const previews = [];
  (imageGroups || []).forEach((group) => {
    const groupUrls = Array.isArray(group?.urls) ? group.urls : [];
    const groupPreviews = Array.isArray(group?.previews) ? group.previews : [];
    const firstUrl = String(groupUrls[0] || groupPreviews[0] || '').trim();
    const firstPreview = String(groupPreviews[0] || groupUrls[0] || '').trim();
    if (!firstUrl && !firstPreview) return;
    urls.push(firstUrl || firstPreview);
    previews.push(firstPreview || firstUrl);
  });
  return { urls, previews };
}

/**
 * 组内拖拽排序：将 fromImageId 插到 toImageId 前面或后面。
 * @param {'before'|'after'} [placement='before']
 */
export function reorderCopywritingGroupImage(
  groups = [],
  groupId,
  fromImageId,
  toImageId,
  placement = 'before',
) {
  const gid = String(groupId || '');
  const fromId = String(fromImageId || '');
  const toId = String(toImageId || '');
  if (!gid || !fromId || !toId || fromId === toId) return groups;
  const placeAfter = placement === 'after';
  return (groups || []).map((group) => {
    if (String(group?.id) !== gid) return group;
    const images = [...(group.images || [])];
    const fromIndex = images.findIndex((img) => String(img?.id) === fromId);
    const toIndex = images.findIndex((img) => String(img?.id) === toId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return group;
    const [moved] = images.splice(fromIndex, 1);
    let insertAt = images.findIndex((img) => String(img?.id) === toId);
    if (insertAt < 0) return group;
    if (placeAfter) insertAt += 1;
    images.splice(insertAt, 0, moved);
    return { ...group, images };
  });
}

/** 组间拖拽：将图片移到目标组的指定下标（toIndex 超出则追加到末尾） */
export function moveCopywritingGroupImage(groups = [], fromGroupId, imageId, toGroupId, toIndex = -1) {
  const fromGid = String(fromGroupId || '');
  const toGid = String(toGroupId || '');
  const id = String(imageId || '');
  if (!fromGid || !toGid || !id) return groups;
  let moved = null;
  const stripped = (groups || []).map((group) => {
    if (String(group?.id) !== fromGid) return group;
    const images = [...(group.images || [])];
    const index = images.findIndex((img) => String(img?.id) === id);
    if (index < 0) return group;
    [moved] = images.splice(index, 1);
    return { ...group, images };
  });
  if (!moved) return groups;
  return stripped.map((group) => {
    if (String(group?.id) !== toGid) return group;
    const images = [...(group.images || [])];
    const insertAt = Number.isFinite(toIndex) && toIndex >= 0
      ? Math.min(toIndex, images.length)
      : images.length;
    images.splice(insertAt, 0, moved);
    return { ...group, images };
  });
}

export const AI_CW_IMAGE_DRAG_MIME = 'application/x-ai-cw-image';

